import { DeleteOutlined, LockOutlined } from '@ant-design/icons';
import { Button, Form, Input, Popconfirm, Tooltip } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { REGEX } from '../common/constants';
import TableComponent from './CommonTable';

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  autoValidate,
  setIsEditing,
  allRows,
  setRowErrors,
  rowErrors,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (autoValidate) {
      setEditing(true);
      form.setFieldsValue({ [dataIndex]: record[dataIndex] });
      setTimeout(() => {
        form.validateFields();
        ref?.current?.focus();
      }, 50);
    }
  }, [autoValidate]);

  const toggleEdit = () => {
    setEditing(!editing);
    setIsEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
    setTimeout(() => {
      ref?.current?.focus({ cursor: 'end' });
    }, 0);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      const trimmedValues = Object.fromEntries(
        Object.entries(values)?.map(([key, value]) => [key, value?.trim()]),
      );
      handleSave({ ...record, ...trimmedValues, isValid: true });
      toggleEdit();
      setRowErrors((prev) => prev?.filter((id) => id !== record?.id));
    } catch (errInfo) {
      setRowErrors((prev) =>
        prev?.includes(record?.id) ? prev : [...prev, record?.id],
      );
    }
  };

  const handleChange = async () => {
    setIsEditing(true);
    try {
      await form.validateFields([dataIndex]);
      setRowErrors((prev) => prev?.filter((id) => id !== record?.id));
    } catch {
      setRowErrors((prev) =>
        prev?.includes(record?.id) ? prev : [...prev, record?.id],
      );
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        name={dataIndex}
        className="m-0"
        rules={[
          {
            validator: (_, value) => {
              if (value && value?.trimStart() !== value) {
                return Promise.reject(
                  new Error('Only spaces are not allowed!'),
                );
              }
              return Promise.resolve();
            },
          },
          ...(dataIndex === 'key'
            ? [
                {
                  validator: (_, value) => {
                    if (!value || value?.trim() === '') {
                      return Promise.reject(new Error('Key is required!'));
                    }
                    if (!REGEX?.ENV_KEY?.test(value)) {
                      return Promise.reject(
                        new Error(
                          'Key must be uppercase with underscores and no spaces.',
                        ),
                      );
                    }
                    const duplicate = allRows?.some(
                      (row) =>
                        row?.id !== record?.id &&
                        row?.key?.trim() === value?.trim(),
                    );

                    if (duplicate) {
                      return Promise.reject(new Error('Key must be unique!'));
                    }
                    return Promise.resolve();
                  },
                },
              ]
            : []),
        ]}
      >
        <Input
          ref={ref}
          onPressEnter={save}
          onBlur={save}
          onChange={handleChange}
        />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap pr-24 editable-hover"
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const EditableTable = ({
  fileContent = [],
  setFileContent,
  setError,
  setIsEditing,
  setRowErrors,
  rowErrors,
}) => {
  const [data, setData] = useState(fileContent ?? []);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      setSelectedRowKeys(keys);

      const updatedData = data?.map((item) => ({
        ...item,
        isPrivate: keys?.includes(item?.id),
      }));

      setData(updatedData);
      setFileContent(updatedData);
    },
    columnTitle: (
      <Tooltip title="Mark as private">
        <LockOutlined className="private-icon" />
      </Tooltip>
    ),
  };

  useEffect(() => {
    const contentWithIds = fileContent?.map((item, index) => ({
      ...item,
      id: item?.id || `${Date.now()}-${index}`,
    }));
    // Find duplicates by key
    const keyMap = new Map();
    contentWithIds?.forEach((item, index) => {
      const trimmedKey = item?.key?.trim();
      if (!trimmedKey) return;
      if (!keyMap?.has(trimmedKey)) {
        keyMap?.set(trimmedKey, [index]);
      } else {
        keyMap?.get(trimmedKey)?.push(index);
      }
    });
    // Mark the last duplicate as invalid
    keyMap?.forEach((indices) => {
      if (indices?.length > 1) {
        const lastIndex = indices[indices?.length - 1];
        contentWithIds[lastIndex].isValid = false;
      }
    });
    setData(contentWithIds);
    setIsEditing(contentWithIds?.some((item) => item?.isValid === false));
  }, [fileContent]);

  const handleSave = (row) => {
    const newData = [...data];
    const index = newData?.findIndex((item) => item?.id === row?.id);
    if (index > -1) {
      newData?.splice(index, 1, { ...newData[index], ...row });
    } else {
      newData?.push({ ...row });
    }
    setData(newData);
    setFileContent(newData);
    setIsEditing(newData?.some((item) => item?.isValid === false));
    setRowErrors((prev) => prev?.filter((id) => id !== row?.id));
  };

  const handleDelete = (key) => {
    const newData = data?.filter((item) => item?.id !== key);
    setData(newData);
    setFileContent(newData);
    setIsEditing(newData?.some((item) => item?.isValid === false));
    setRowErrors((prev) => prev?.filter((id) => id !== key));
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = [
    {
      title: 'Key',
      dataIndex: 'key',
      editable: true,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      editable: true,
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 100,
      align: 'center',
      render: (_, record) =>
        data.length >= 1 ? (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => handleDelete(record?.id)}
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              className="delete-icon"
            />
          </Popconfirm>
        ) : null,
    },
  ]?.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
        autoValidate: col.dataIndex === 'key' && record?.isValid === false,
        setIsEditing,
        allRows: data,
        setRowErrors,
        rowErrors,
      }),
    };
  });

  useEffect(() => {
    if (!data?.some((item) => item?.isValid === false)) {
      setError?.(null);
    }
  }, [data, setError]);

  return (
    <Form validateTrigger="onChange" component={false}>
      <TableComponent
        components={components}
        rowClassName={() => 'editable-row'}
        bordered
        data={data}
        columns={columns}
        pagination={false}
        scroll={{ y: 'calc(100vh - 280px)' }}
        rowKey="id"
        rowSelection={rowSelection}
      />
    </Form>
  );
};

export default EditableTable;
